import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';

export interface TrustedDeviceState {
  data: TrustedDevice[];
  loading: boolean;
  error: string | null;
}

export interface TrustedDevice {
  id: number;
  derived_name: string;
  status: TrustedDeviceStatus;
  country: string;
  user_agent: string;
  ip_address: string;
  trusted_at: string | null;
  computed_revoking?: boolean;
  nonce: string;
}

export enum TrustedDeviceStatus {
  Trusted = 'trusted',
  Untrusted = 'untrusted',
  TrustedOnce = 'trusted_once',
}

@Injectable({
  providedIn: 'root',
})
export class TrustedDevicesService {
  #fetchTrustedDevicesUrl = `${environment.apiUrl}/login-devices`;

  #trustedDeviceState: BehaviorSubject<TrustedDeviceState> = new BehaviorSubject<TrustedDeviceState>({
    data: [],
    loading: false,
    error: null,
  });
  trustedDeviceState$ = this.#trustedDeviceState.asObservable();

  constructor(private http: HttpClient) {}

  fetchTrustedDevicesFromApi(): void {
    this.#trustedDeviceState.next({ ...this.#trustedDeviceState.getValue(), loading: true });

    this.http
      .get<{ data: TrustedDevice[] }>(this.#fetchTrustedDevicesUrl)
      .pipe(
        map((res) => ({
          data: res.data,
          loading: false,
          error: null,
        })),
        catchError(() => {
          return of({
            data: [],
            loading: false,
            error: 'Failed to load trusted devices',
          });
        })
      )
      .subscribe((state) => {
        this.#trustedDeviceState.next(state);
      });
  }

  revokeDevice(deviceId: TrustedDevice['id']) {
    this.http
      .delete<{ success: boolean }>(`${this.#fetchTrustedDevicesUrl}/${deviceId}`)
      .pipe(
        map(() => {
          const currentState = this.#trustedDeviceState.getValue();
          const newData = currentState.data.filter((device) => device.id !== deviceId);
          return { ...currentState, data: newData };
        }),
        startWith({
          ...this.#trustedDeviceState.getValue(),
          data: this.#trustedDeviceState.getValue().data.map((device) => {
            if (device.id === deviceId) {
              device.computed_revoking = true;
            }
            return device;
          }),
        })
      )
      .subscribe((state) => {
        this.#trustedDeviceState.next(state);
      });
  }

  stubDevices(): TrustedDevice[] {
    return [
      {
        id: 1,
        derived_name: 'Chrome on Windows',
        status: TrustedDeviceStatus.Trusted,
        country: 'US',
        user_agent:
          'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.3',
        ip_address: '127.0.0.1',
        trusted_at: '2021-01-01T00:00:00Z',
        nonce: '1234-1234-1234',
      },
    ];
  }
}
